import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  ArrowLeft,
  Info,
  Activity,
  Cloud,
  Network,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Check,
  AlertTriangle,
  Lightbulb,
  ShieldMinus,
  Sparkles,
  Loader2,
  ScanSearch,
} from "lucide-react";
import { SiGooglecloud, SiAmazon } from "react-icons/si";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import { Skeleton } from "../../ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { AreaChart, Area, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from "recharts";
import { useToast } from "../../../hooks/use-toast";
import { NGROK } from "../../../APIs";
import { myLocalStorage } from "../../StorageHelper";
import axios from "axios";
import { useQuery, useQueryClient } from "react-query";
import CloudRecentActivity from "./CloudRecentActivity";
import CloudCorrelations from "./CloudCorrelations";
import SecretDiscovery from "./SecretDiscovery";
import { fetchSecretDiscovery } from "./secretDiscoveryService";
import { getAnalyticsApis } from "./helper";
import { CloudAnomalyAlert } from "../CloudSightAlerts/types/alert";
import { AlertGrouping } from "../CloudSightAlerts/components/alerts/AlertGrouping";
import { AlertDetails } from "../CloudSightAlerts/components/alerts/AlertDetails";
import { analyzeEntity } from "../Cloud/statusHelpers";
import {
  LLM_PROVIDER_KEY_NOT_CONFIGURED_MESSAGE,
  LLM_TASK_ALREADY_IN_PROGRESS_MESSAGE,
  useLLMProviderAvailability,
} from "../hooks/useLLMProviderAvailability";
import "./CloudEntityAnalytics.css";

type CloudProvider = "gcp" | "aws" | "azure";
type EntityType = "user" | "nhi";
type RoleOperation = "resolve" | "suggest" | "role";

interface CloudEntityRole {
  role: string;
  scope?: string;
}

interface CloudEntityAnalyticsProps {
  cloudProvider: CloudProvider;
  entityId: string;
  entityType: EntityType;
  projectId: string;
  roles: any[];
  syncedAt: any;
  email: string;
  entityName: string;
  cloudNHIType: string | "";
  details: any;
  extrasdetails: any;
}

const reconstructSentences = (arr: string[]): string[] => {
  if (!arr || !Array.isArray(arr)) return [];
  const result: string[] = [];
  let currentStr = "";

  for (let i = 0; i < arr.length; i++) {
    const part = arr[i];
    
    if (currentStr === "") {
      currentStr = part;
    } else {
      currentStr += "," + part;
    }

    // Determine if the next element is likely a continuation (starts with a space)
    const isNextContinuation = i + 1 < arr.length && arr[i + 1].startsWith(" ");
    
    if (!isNextContinuation) {
      result.push(currentStr);
      currentStr = "";
    }
  }
  
  return result;
};

const cloudConfig = {
  gcp: {
    name: "GCP",
    icon: SiGooglecloud,
    color: "text-blue-600",
  },
  aws: {
    name: "AWS",
    icon: SiAmazon,
    color: "text-orange-600",
  },
  azure: {
    name: "Azure",
    icon: Cloud,
    color: "text-blue-500",
  },
};

const DONUT_COLORS = [
  "#3B82F6",
  "#10B981",
  "#F59E0B",
  "#8B5CF6",
  "#EC4899",
  "#F97316",
  "#06B6D4",
  "#84CC16",
  "#EAB308",
  "#A855F7",
];

const normalizeActor = (value: unknown) => String(value || "").trim();

const isServiceAccountActor = (actor: string) => {
  const normalized = actor.toLowerCase();
  return (
    normalized.includes("gserviceaccount.com") ||
    normalized.includes("serviceaccount:") ||
    normalized.includes("managed identity") ||
    normalized.includes("assumed-role") ||
    normalized.includes("role/")
  );
};

const deriveActorName = (log: any) => {
  const raw = log?.rawLog || {};
  const protoPayload = raw?.protoPayload || {};
  const authenticationInfo = protoPayload?.authentication_info || {};
  const requestMetadata = protoPayload?.request_metadata || {};
  const userIdentity = raw?.userIdentity || {};

  return normalizeActor(
    authenticationInfo?.principal_email ||
    userIdentity?.arn ||
    userIdentity?.userName ||
    userIdentity?.principalId ||
    raw?.identity ||
    raw?.actor ||
    log?.actor ||
    log?.entity ||
    requestMetadata?.caller_supplied_user_agent
  );
};

const aggregateActors = (logs: any[], actorType: "user" | "service-account") => {
  const counts = new Map<string, number>();

  logs.forEach((log) => {
    const actor = deriveActorName(log);
    if (!actor) return;

    const serviceAccount = isServiceAccountActor(actor);
    if (actorType === "service-account" && !serviceAccount) return;
    if (actorType === "user" && serviceAccount) return;

    counts.set(actor, (counts.get(actor) || 0) + 1);
  });

  return Array.from(counts.entries())
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10);
};

const flattenRoleSource = (source: any): any[] => {
  if (!source) return [];

  if (Array.isArray(source)) {
    return source.flatMap((item) => flattenRoleSource(item));
  }

  if (typeof source === "string") {
    return source
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (
    typeof source === "object" &&
    (
      source?.roleName ||
      source?.roleArn ||
      source?.roleARN ||
      source?.arn ||
      source?.title ||
      source?.role ||
      source?.roleId ||
      source?.roleDefinitionName
    )
  ) {
    return [source];
  }

  return [];
};

const normalizeRoleLabel = (role: any) => {
  if (!role) return "";

  if (typeof role === "string") {
    return role.trim();
  }

  return String(
    role?.roleName ||
    role?.title ||
    role?.role ||
    role?.roleDefinitionName ||
    role?.roleId ||
    role?.roleArn ||
    role?.roleARN ||
    role?.arn ||
    "",
  ).trim();
};

const formatGcpRoleName = (roleId: string) => {
  const rawName = String(roleId || "").trim().split("/").pop() || "";
  const words = rawName
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[._-]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const labels: Record<string, string> = {
    ai: "AI",
    api: "API",
    bigquery: "BigQuery",
    cloudsql: "Cloud SQL",
    dns: "DNS",
    iam: "IAM",
    iap: "IAP",
    ip: "IP",
    ml: "ML",
    oauth: "OAuth",
    os: "OS",
    pubsub: "Pub/Sub",
    sql: "SQL",
    ssl: "SSL",
    tls: "TLS",
    vm: "VM",
    vpn: "VPN",
  };
  return words
    .map((word) => labels[word.toLowerCase()] || `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(" ");
};

const isSameRole = (first: string, second: string) => {
  const left = String(first || "").trim();
  const right = String(second || "").trim();
  if (left.toLowerCase() === right.toLowerCase()) return true;
  if (!left.startsWith("arn:aws:") && !right.startsWith("arn:aws:")) return false;
  return left.substring(left.lastIndexOf("/") + 1).toLowerCase() ===
    right.substring(right.lastIndexOf("/") + 1).toLowerCase();
};

const extractDisplayRoles = (...sources: any[]) =>
  Array.from(
    new Set(
      sources
        .flatMap((source) => flattenRoleSource(source))
        .map((role) => normalizeRoleLabel(role))
        .filter(Boolean),
    ),
  );

const parseDateValue = (value: any) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const normalizedValue = String(value || "").trim();

  if (
    !normalizedValue ||
    normalizedValue === "-" ||
    normalizedValue.toLowerCase() === "n/a"
  ) {
    return null;
  }

  const protobufSeconds = normalizedValue.match(/seconds:\s*(\d+)/)?.[1];
  const protobufNanos = normalizedValue.match(/nanos:\s*(\d+)/)?.[1];

  if (protobufSeconds) {
    const parsedDate = new Date(
      Number(protobufSeconds) * 1000 + Number(protobufNanos || 0) / 1e6,
    );
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const nativeDate = new Date(normalizedValue);

  if (!Number.isNaN(nativeDate.getTime())) {
    return nativeDate;
  }

  const dayFirstMatch = normalizedValue.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,\s*|\s+)?(\d{1,2})(?::(\d{2}))(?::(\d{2}))?(?:\s*(AM|PM))?$/i,
  );

  if (dayFirstMatch) {
    const [, day, month, year, hours, minutes, seconds, meridiem] =
      dayFirstMatch;
    let normalizedHours = Number(hours);

    if (meridiem) {
      const normalizedMeridiem = meridiem.toUpperCase();
      if (normalizedMeridiem === "PM" && normalizedHours < 12) {
        normalizedHours += 12;
      }
      if (normalizedMeridiem === "AM" && normalizedHours === 12) {
        normalizedHours = 0;
      }
    }

    const parsedDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      normalizedHours,
      Number(minutes),
      Number(seconds || 0),
    );

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  const dateOnlyMatch = normalizedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

  if (dateOnlyMatch) {
    const [, day, month, year] = dateOnlyMatch;
    const parsedDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
    );

    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  }

  return null;
};

const formatDisplayDate = (value: any) => {
  const parsedDate = parseDateValue(value);
  return parsedDate ? parsedDate.toLocaleString("en-GB") : "";
};

export default function CloudEntityAnalytics({
  cloudProvider,
  entityId,
  entityType,
  projectId,
  roles,
  syncedAt,
  email,
  entityName,
  cloudNHIType,
  details = {},
  extrasdetails = {},
}: CloudEntityAnalyticsProps) {



  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<"discovery" | "overview" | "details" | "activity" | "correlations">(
    () => entityType === "nhi" && ["SECRET", "K8SECRET"].includes(String(cloudNHIType || "").toUpperCase())
      ? "discovery"
      : "details",
  );
  const [detachDialogOpen, setDetachDialogOpen] = useState(false);
  const [selectedDetach, setSelectedDetach] = useState<{
    type: RoleOperation;
    role: string;
    resource?: string;
    replacementRoles?: string[];
  } | null>(null);
  const [activeOperation, setActiveOperation] = useState<{
    type: "resolve" | "suggest";
    role: string;
    resource?: string;
    replacementRoles?: string[];
  } | null>(null);
  const [isSubmittingOperation, setIsSubmittingOperation] = useState(false);
  const [locallyRemovedRoles, setLocallyRemovedRoles] = useState<string[]>([]);
  const [locallyAddedRoles, setLocallyAddedRoles] = useState<string[]>([]);
  const [resolutionPage, setResolutionPage] = useState(1);
  const [suggestionPage, setSuggestionPage] = useState(1);
  const [rolesExpanded, setRolesExpanded] = useState(false);
  const ITEMS_PER_PAGE = 5;

  const [groupingMode, setGroupingMode] = useState<"alertType" | "alert" | "entity">("alert");
  const [selectedAlert, setSelectedAlert] = useState<CloudAnomalyAlert | null>(null);
  const [isAlertDetailsOpen, setIsAlertDetailsOpen] = useState(false);
  const [alertSearchValue, setAlertSearchValue] = useState("");
  const [alertSeverityFilter, setAlertSeverityFilter] = useState<string[]>([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    const pageEl = document.querySelector('.cea-page');
    if (pageEl && pageEl.parentElement) {
      pageEl.parentElement.scrollTo(0, 0);
    }
  }, []);


  const analyzed = useMemo(() => {
    return analyzeEntity({
      key: email || entityId,
      name: entityName,
      subtitle: email,
      roles: roles,
      activity: 0,
      type: entityType,
      raw: details
    });
  }, [email, entityId, entityName, roles, entityType, details]);

  const triggeredColor = analyzed.status === "improved" ? "hsl(142, 55%, 48%)" : "hsl(0, 62%, 55%)";
  const corrColor = "hsl(220, 65%, 55%)";

  const latestTenant =
    details?.tenantName ||
    details?.wsTenantName ||
    myLocalStorage.getItem("latestTenant")?.tenantName ||
    "";

  const CloudIcon = cloudConfig[cloudProvider]?.icon ?? Cloud;
  const latestSubListRaw = myLocalStorage.getItem("latestSubList");
  const latestSubList = latestSubListRaw ? JSON.parse(latestSubListRaw) : null;
  const gcpProjectId = myLocalStorage.getItem("gcpSelectedProjectId");
  const accountId = myLocalStorage.getItem("selectedAwsAccount");
  const subscriptionId = latestSubList?.azureSubscriptionId;
  const resolvedProjectId =
    cloudProvider.toUpperCase() === "GCP"
      ? gcpProjectId || projectId
      : cloudProvider.toUpperCase() === "AZURE"
        ? subscriptionId || projectId
        : accountId || projectId;
  const normalizedCloudNhiType = String(cloudNHIType || "").toUpperCase();
  const gcpServiceAccountEmail =
    details?.serviceAccountEmail ||
    details?.item?.serviceAccountEmail ||
    details?.item?.email ||
    details?.sa?.serviceAccountEmail ||
    details?.sa?.email ||
    email ||
    entityId ||
    "";
  const shouldFetchGcpNhiRoles =
    cloudProvider === "gcp" &&
    entityType === "nhi" &&
    ![
      "SECRET",
      "TOKEN",
      "K8SERVICE",
      "K8TOKEN",
      "K8SECRET",
      "OIDC",
    ].includes(normalizedCloudNhiType) &&
    Boolean(latestTenant && resolvedProjectId && gcpServiceAccountEmail);
  const azureUserAnalyticsIdentifier =
    details?.userDetails?.mail ||
    details?.mail ||
    details?.email ||
    email ||
    details?.userPrincipalName ||
    entityId;
  const analyticsEntity =
    cloudProvider === "azure"
      ? entityType === "user"
        ? azureUserAnalyticsIdentifier
        : details?.principalId || details?.serviceDetails?.principalId || email || entityId
      : cloudProvider === "gcp"
        ? email || entityId
        : entityId || email;

  const {
    resourceApi,
    resolutionApi,
    permissionApi,
    suggestionApi,
    rawLogsAPI,
    secretAnalyticsApi,
  } = getAnalyticsApis({
    entityType,
    cloudProvider,
    latestTenant,
    projectId: resolvedProjectId,
    email: analyticsEntity,
    cloudNHIType,
    details,
    extrasdetails,
  });

  const {
    data: gcpNhiRolesMap,
    isLoading: isGcpNhiRolesLoading,
    isFetching: isGcpNhiRolesFetching,
    isFetched: hasFetchedGcpNhiRoles,
  } = useQuery(
    ["gcp-nhi-roles", latestTenant, resolvedProjectId],
    async () => {
      const response = await axios.get(
        `${NGROK}/api/gcp/get-all-non-human-identities-roles`,
        {
          params: {
            tenantName: latestTenant,
            projectId: resolvedProjectId,
          },
        },
      );

      return response.data || {};
    },
    {
      enabled: shouldFetchGcpNhiRoles,
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const { data: gcpRoleNames = {} } = useQuery<Record<string, string>>(
    ["gcp-role-names", latestTenant, resolvedProjectId],
    async () => {
      const response = await axios.get(`${NGROK}/api/gcp/resource/role-names`, {
        params: {
          tenantName: latestTenant,
          projectId: resolvedProjectId,
        },
      });
      return response.data || {};
    },
    {
      enabled:
        cloudProvider === "gcp" &&
        activeTab === "details" &&
        Boolean(latestTenant && resolvedProjectId),
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const gcpRoleNameLookup = useMemo(
    () =>
      Object.entries(gcpRoleNames).reduce<Record<string, string>>((lookup, [roleId, roleName]) => {
        lookup[roleId.toLowerCase()] = roleName;
        return lookup;
      }, {}),
    [gcpRoleNames],
  );

  const getRoleDisplayName = useCallback(
    (role: string) => {
      const roleId = String(role || "").trim();
      if (!roleId || cloudProvider !== "gcp") return roleId;
      const configuredName = gcpRoleNameLookup[roleId.toLowerCase()];
      if (configuredName) return configuredName;
      return roleId.startsWith("roles/") || roleId.includes("/roles/")
        ? formatGcpRoleName(roleId)
        : roleId;
    },
    [cloudProvider, gcpRoleNameLookup],
  );


  const { data: resourceUsage = {}, isLoading: isResourceUsageLoading } = useQuery({
    queryKey: [
      "user-resource-usage",
      cloudProvider,
      analyticsEntity,
      resolvedProjectId,
      latestTenant,
    ],
    queryFn: async () => {
      const { data } = await axios.get(resourceApi);
      return data || {};
    },
    enabled: !!resourceApi,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const { data: permissionUsage = {}, isLoading: isPermissionUsageLoading } = useQuery({
    queryKey: [
      "user-permission-usage",
      cloudProvider,
      analyticsEntity,
      resolvedProjectId,
      latestTenant,
    ],
    queryFn: async () => {
      const { data } = await axios.get(permissionApi);
      return data || {};
    },
    enabled: !!permissionApi,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const resolutionQueryKey = useMemo(() => [
    "user-analytics-resolutions",
    cloudProvider,
    analyticsEntity,
    resolvedProjectId,
    latestTenant,
  ], [cloudProvider, analyticsEntity, resolvedProjectId, latestTenant]);
  const suggestionQueryKey = useMemo(() => [
    "user-analytics-suggestions",
    cloudProvider,
    analyticsEntity,
    resolvedProjectId,
    latestTenant,
  ], [cloudProvider, analyticsEntity, resolvedProjectId, latestTenant]);

  const { data: resolutions = {} } = useQuery({
    queryKey: resolutionQueryKey,
    queryFn: async () => {
      const { data } = await axios.get(resolutionApi);
      return data || {};
    },
    enabled: !!resolutionApi,
    refetchInterval: 3000,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const { data: suggestions = {} } = useQuery({
    queryKey: suggestionQueryKey,
    queryFn: async () => {
      const { data } = await axios.get(suggestionApi);
      return data || {};
    },
    enabled: !!suggestionApi,
    refetchInterval: 3000,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const { data: gcpSecretAnalytics = {}, isLoading: isGcpSecretAnalyticsLoading } = useQuery({
    queryKey: [
      "gcp-secret-analytics",
      cloudProvider,
      entityId,
      resolvedProjectId,
      latestTenant,
    ],
    queryFn: async () => {
      const { data } = await axios.get(secretAnalyticsApi!);
      return data || {};
    },
    enabled: !!secretAnalyticsApi,
  });

  const { data: secretRawLogs = [] } = useQuery({
    queryKey: [
      "secret-raw-logs",
      cloudProvider,
      entityId,
      resolvedProjectId,
      latestTenant,
    ],
    queryFn: async () => {
      if (!rawLogsAPI) return [];
      const { data } = await axios.get(rawLogsAPI);
      return Array.isArray(data) ? data : [];
    },
    enabled: !!rawLogsAPI && (cloudNHIType === "SECRET" || cloudNHIType === "K8SECRET"),
  });

  const { data: entityCardsRaw, isLoading: isEntityCardsLoading } = useQuery({
    queryKey: [
      "entity-cards",
      cloudProvider,
      resolvedProjectId,
      latestTenant,
      analyticsEntity,
    ],
    queryFn: async () => {
      const response = await axios.get(`${NGROK}/api/cloud/entity-cards`, {
        params: {
          tenantName: latestTenant,
          cloudAccountId: resolvedProjectId,
          entity: analyticsEntity,
          cloudType: cloudProvider.toUpperCase(),
        },
      });
      return response.data;
    },
    enabled: !!latestTenant && !!resolvedProjectId && !!analyticsEntity && (activeTab === "details" || activeTab === "overview"),
  });

  const queryClient = useQueryClient();
  const entityRiskParam = cloudProvider === "gcp"
    ? email
    : cloudProvider === "azure" && entityType === "nhi"
      ? details?.principalId || details?.serviceDetails?.principalId || entityId
      : entityId;
  const entityRoleQueryKey = useMemo(() => [
    "cloud-entity-roles",
    cloudProvider,
    entityRiskParam,
    resolvedProjectId,
    latestTenant,
  ], [cloudProvider, entityRiskParam, resolvedProjectId, latestTenant]);

  const {
    data: entityRoles = [],
    isFetched: hasFetchedEntityRoles,
  } = useQuery<CloudEntityRole[]>({
    queryKey: entityRoleQueryKey,
    queryFn: async () => {
      const { data } = await axios.get(`${NGROK}/api/cloud/entity-roles`, {
        params: {
          tenantName: latestTenant,
          cloudAccountId: resolvedProjectId,
          entity: entityRiskParam,
          cloudType: cloudProvider.toUpperCase(),
        },
      });
      return Array.isArray(data) ? data : [];
    },
    enabled:
      activeTab === "details" &&
      !!latestTenant &&
      !!resolvedProjectId &&
      !!entityRiskParam &&
      !(entityType === "nhi" && ["SECRET", "K8SECRET"].includes(normalizedCloudNhiType)),
    refetchInterval: 3000,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const { data: llmTaskStatuses = [] } = useQuery({
    queryKey: ["llm-task-status", latestTenant, resolvedProjectId],
    queryFn: async () => {
      const { data } = await axios.get(`${NGROK}/api/llm-task/status`, {
        params: {
          tenantName: latestTenant,
          cloudAccountId: resolvedProjectId,
        },
      });
      return data || [];
    },
    enabled: !!latestTenant && !!resolvedProjectId && activeTab === "overview",
    refetchInterval: 1000,
  });

  const riskInsightStatus = Array.isArray(llmTaskStatuses)
    ? llmTaskStatuses.find((task: any) => task?.llmTask === "GENERATE_ENTITY_RISK_INSIGHTS")?.status
    : undefined;
  const isRiskInsightInProgress = riskInsightStatus === "IN_PROGRESS";

  const [isTriggeringRiskInsight, setIsTriggeringRiskInsight] = useState(false);
  const prevRiskInsightStatusRef = useRef<string | undefined>(undefined);
  const { hasConfiguredLLMProviderKey } = useLLMProviderAvailability();

  useEffect(() => {
    if (prevRiskInsightStatusRef.current === "IN_PROGRESS" && riskInsightStatus && riskInsightStatus !== "IN_PROGRESS") {
      queryClient.invalidateQueries(["entity-cards", cloudProvider, resolvedProjectId, latestTenant, analyticsEntity]);
    }
    prevRiskInsightStatusRef.current = riskInsightStatus;
  }, [riskInsightStatus]);

  const handleGenerateRiskInsights = async () => {
    if (isRiskInsightInProgress || isTriggeringRiskInsight || !hasConfiguredLLMProviderKey) return;
    if (!latestTenant || !resolvedProjectId || !analyticsEntity) return;
    try {
      setIsTriggeringRiskInsight(true);
      await axios.post(`${NGROK}/api/cloud/generate-entity-risk-insights`, null, {
        params: {
          tenantName: latestTenant,
          cloudAccountId: resolvedProjectId,
          cloudType: cloudProvider.toUpperCase(),
          entity: analyticsEntity,
        },
        timeout: 600000,
      });
      queryClient.invalidateQueries(["llm-task-status", latestTenant, resolvedProjectId]);
      queryClient.invalidateQueries(["entity-cards", cloudProvider, resolvedProjectId, latestTenant, analyticsEntity]);
    } catch (error) {
      queryClient.invalidateQueries(["llm-task-status", latestTenant, resolvedProjectId]);
    } finally {
      setIsTriggeringRiskInsight(false);
    }
  };
  const riskInsightBlockedReason = (isRiskInsightInProgress || isTriggeringRiskInsight)
    ? LLM_TASK_ALREADY_IN_PROGRESS_MESSAGE
    : !hasConfiguredLLMProviderKey
      ? LLM_PROVIDER_KEY_NOT_CONFIGURED_MESSAGE
      : '';
  const isRiskInsightButtonDisabled =
    isRiskInsightInProgress ||
    isTriggeringRiskInsight ||
    !latestTenant ||
    !resolvedProjectId ||
    !analyticsEntity ||
    !hasConfiguredLLMProviderKey;

  const entityCardsData = entityCardsRaw || {};
  const alertsTriggeredMap = entityCardsData.alertsTriggered || {};
  const alertsDates = Object.keys(alertsTriggeredMap).sort();
  const saasCorrMap = entityCardsData.saasCorrelations || {};
  const saasDates = Object.keys(saasCorrMap).sort();

  const potentialRisks = reconstructSentences(entityCardsData.potentialRisks || []);
  const insights = reconstructSentences(entityCardsData.insights || []);
  
  const addedRoles = entityCardsData.addedRoles || [];
  const removedRoles = entityCardsData.removedRoles || [];

  const formatShortDate = (dateStr: string) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };
  
  const alertsData = alertsDates.map((d) => ({
    name: formatShortDate(d),
    value: alertsTriggeredMap[d]
  }));

  const saasData = saasDates.map((d) => ({
    name: formatShortDate(d),
    value: saasCorrMap[d]
  }));

  const maxAlerts = alertsData.length > 0 ? Math.max(...alertsData.map(d => Number(d.value) || 0)) : 0;
  const maxSaas = saasData.length > 0 ? Math.max(...saasData.map(d => Number(d.value) || 0)) : 0;
  const showAlertsTriggered = isEntityCardsLoading || maxAlerts > 0;
  const showSaasCorrelations = isEntityCardsLoading || maxSaas > 0;

  const { data: rawAlerts = [], isLoading: isAlertsLoading } = useQuery({
    queryKey: [
      "entity-alerts",
      cloudProvider,
      resolvedProjectId,
      latestTenant,
      analyticsEntity,
    ],
    queryFn: async () => {
      const response = await axios.get(`${NGROK}/api/cloud/get-entity-anomalies-group`, {
        params: {
          tenantName: latestTenant,
          cloudId: resolvedProjectId,
          cloudType: cloudProvider?.toUpperCase(),
          entity: analyticsEntity,
          timeRangeCode: 7,
          groupBy: "alertType",
        },
      });
      const groupRaw = response.data || [];
      let flattened: CloudAnomalyAlert[] = [];
      if (Array.isArray(groupRaw)) {
        flattened = groupRaw;
      } else {
        Object.values(groupRaw).forEach((value) => {
          if (Array.isArray(value)) flattened = flattened.concat(value as CloudAnomalyAlert[]);
        });
      }
      return flattened;
    },
    enabled: !!latestTenant && !!resolvedProjectId && !!analyticsEntity && (activeTab === "details" || activeTab === "overview"),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });

  const entityResolutions = resolutions as any;
  const entitySuggestions = suggestions as any;
  const resolutionInProgress = entityResolutions?.status === "IN_PROGRESS";
  const suggestionInProgress = entitySuggestions?.status === "IN_PROGRESS";

  const analyticsResourceUsage =
    secretAnalyticsApi && cloudNHIType === "SECRET"
      ? (gcpSecretAnalytics as any)?.topResources || {}
      : resourceUsage;

  const analyticsPermissionUsage =
    secretAnalyticsApi && cloudNHIType === "SECRET"
      ? (gcpSecretAnalytics as any)?.topPermissions || {}
      : permissionUsage;

  const totalResourceUsage = Object.values(analyticsResourceUsage).reduce(
    (sum: number, val) => sum + Number(val),
    0
  );

  const totalPermissionUsage = Object.values(analyticsPermissionUsage).reduce(
    (sum: number, val) => sum + Number(val),
    0
  );

  const topResources = Object.entries(analyticsResourceUsage)
    .sort(([, a], [, b]) => Number(b) - Number(a))
    .slice(0, 10);

  const topPermissions = Object.entries(analyticsPermissionUsage)
    .sort(([, a], [, b]) => Number(b) - Number(a))
    .slice(0, 10);

  const isTopResourcesLoading =
    secretAnalyticsApi && cloudNHIType === "SECRET"
      ? isGcpSecretAnalyticsLoading
      : isResourceUsageLoading;

  const isTopPermissionsLoading =
    secretAnalyticsApi && cloudNHIType === "SECRET"
      ? isGcpSecretAnalyticsLoading
      : isPermissionUsageLoading;

  const isSecretAnalytics =
    entityType === "nhi" &&
    (cloudNHIType === "SECRET" || cloudNHIType === "K8SECRET");

  const topSecretUsers = useMemo(
    () =>
      secretAnalyticsApi && cloudNHIType === "SECRET"
        ? Object.entries((gcpSecretAnalytics as any)?.topUsers || {}).sort(([, a], [, b]) => Number(b) - Number(a)).slice(0, 10)
        : aggregateActors(secretRawLogs as any[], "user"),
    [secretRawLogs, secretAnalyticsApi, gcpSecretAnalytics, cloudNHIType]
  );

  const topSecretServiceAccounts = useMemo(
    () =>
      secretAnalyticsApi && cloudNHIType === "SECRET"
        ? Object.entries((gcpSecretAnalytics as any)?.topServiceAccounts || {}).sort(([, a], [, b]) => Number(b) - Number(a)).slice(0, 10)
        : aggregateActors(secretRawLogs as any[], "service-account"),
    [secretRawLogs, secretAnalyticsApi, gcpSecretAnalytics, cloudNHIType]
  );

  const resolutionItems = Object.entries(
    entityResolutions.resolutions || {}
  ).map(([currentRole, resources]) => {
    const resolution = resources as Record<string, unknown>;
    const projectRoles = Array.isArray(resolution.roles)
      ? resolution.roles.map(String)
      : [];
    const suggestedRoles = projectRoles.length > 0
      ? projectRoles
      : Object.values(resolution).flatMap((roleValue) =>
          Array.isArray(roleValue) ? roleValue.map(String) : [String(roleValue)],
        );
    return {
      currentRole,
      suggestedRoles: Array.from(new Set(suggestedRoles.filter(Boolean))),
    };
  }).filter((item) => item.suggestedRoles.length > 0);


  const suggestionItems = Object.entries(
    entitySuggestions.suggestions || {}
  ).flatMap(([level, roleValue]) => {
    const rolesStr = String(roleValue);
    const rolesArray = rolesStr.split(',').map(r => r.trim()).filter(Boolean);
    return rolesArray.map(role => ({
      level: String(level),
      role
    }));
  });

  const totalResolutionPages = Math.ceil(
    resolutionItems.length / ITEMS_PER_PAGE
  );
  const totalSuggestionPages = Math.ceil(
    suggestionItems.length / ITEMS_PER_PAGE
  );

  const paginatedResolutionItems = resolutionItems.slice(
    (resolutionPage - 1) * ITEMS_PER_PAGE,
    resolutionPage * ITEMS_PER_PAGE
  );
  const paginatedSuggestionItems = suggestionItems.slice(
    (suggestionPage - 1) * ITEMS_PER_PAGE,
    suggestionPage * ITEMS_PER_PAGE
  );

  const {
    cloudType,
    secretDetails,
    tokenDetails,
    cloudTokenDetails,
    serviceDetails,
    serviceAccountEmail,
    principalId,
    secretName,
    oidcARN,
    oidc,
  } = details;
  const displayRoles = useMemo(
    () =>
      extractDisplayRoles(
        roles,
        details?.roles,
        details?.serviceroles,
        details?.serviceRoles,
        details?.roleDefinitions,
        details?.roleNames,
        details?.roleDetails,
        details?.iamRoleDetails,
        details?.serviceDetails,
        details?.serviceDetails?.roles,
        details?.serviceDetails?.serviceroles,
        details?.serviceDetails?.serviceRoles,
        details?.serviceDetails?.roleDefinitions,
        details?.serviceDetails?.roleDetails,
        details?.serviceDetails?.iamRoleDetails,
        details?.item,
        details?.item?.roles,
        details?.item?.serviceroles,
        details?.item?.serviceRoles,
        details?.item?.roleDefinitions,
        details?.item?.roleNames,
        details?.item?.roleDetails,
        details?.item?.iamRoleDetails,
        details?.item?.serviceDetails,
        details?.item?.serviceDetails?.roles,
        details?.item?.serviceDetails?.serviceroles,
        details?.item?.serviceDetails?.serviceRoles,
        details?.item?.serviceDetails?.roleDefinitions,
        details?.item?.serviceDetails?.roleDetails,
        details?.item?.serviceDetails?.iamRoleDetails,
        details?.sa,
        details?.sa?.roles,
        details?.sa?.serviceroles,
        details?.sa?.serviceRoles,
        details?.sa?.roleDefinitions,
        details?.sa?.roleNames,
        details?.sa?.roleDetails,
        details?.sa?.iamRoleDetails,
        details?.sa?.serviceDetails,
        details?.sa?.serviceDetails?.roles,
        details?.sa?.serviceDetails?.serviceroles,
        details?.sa?.serviceDetails?.serviceRoles,
        details?.sa?.serviceDetails?.roleDefinitions,
        details?.sa?.serviceDetails?.roleDetails,
        details?.sa?.serviceDetails?.iamRoleDetails,
        extrasdetails?.roles,
        extrasdetails?.serviceroles,
        extrasdetails?.serviceRoles,
        extrasdetails?.roleDefinitions,
        extrasdetails?.roleNames,
        extrasdetails?.roleDetails,
        extrasdetails?.iamRoleDetails,
        extrasdetails?.serviceDetails,
        extrasdetails?.serviceDetails?.roles,
        extrasdetails?.serviceDetails?.serviceroles,
        extrasdetails?.serviceDetails?.serviceRoles,
        extrasdetails?.serviceDetails?.roleDefinitions,
        extrasdetails?.serviceDetails?.roleDetails,
        extrasdetails?.serviceDetails?.iamRoleDetails,
      ),
    [details, extrasdetails, roles],
  );
  const gcpFetchedRoles = useMemo(
    () => {
      if (!shouldFetchGcpNhiRoles || !gcpNhiRolesMap) {
        return [];
      }

      const roleSources = [
        gcpNhiRolesMap?.[gcpServiceAccountEmail],
        gcpNhiRolesMap?.[details?.serviceAccountEmail],
        gcpNhiRolesMap?.[serviceAccountEmail],
        gcpNhiRolesMap?.[email],
        gcpNhiRolesMap?.[entityId],
      ];

      return extractDisplayRoles(...roleSources);
    },
    [
      details?.serviceAccountEmail,
      email,
      entityId,
      gcpNhiRolesMap,
      gcpServiceAccountEmail,
      serviceAccountEmail,
      shouldFetchGcpNhiRoles,
    ],
  );
  const shouldDelayGcpRoleDisplay =
    shouldFetchGcpNhiRoles &&
    !hasFetchedGcpNhiRoles &&
    (isGcpNhiRolesLoading || isGcpNhiRolesFetching);
  const entityRoleLabels = useMemo(
    () => entityRoles.map((item) => getRoleDisplayName(item?.role)).filter(Boolean),
    [entityRoles, getRoleDisplayName],
  );
  const currentEntityRoles = useMemo(() => {
    const distinct = new Map<string, CloudEntityRole>();
    entityRoles
      .filter((item) => item?.role && !locallyRemovedRoles.some((role) => isSameRole(role, item.role)))
      .forEach((item) => distinct.set(`${item.role}\u0000${item.scope || ""}`, item));
    return Array.from(distinct.values());
  }, [entityRoles, locallyRemovedRoles]);
  const baseDisplayRoles =
    hasFetchedEntityRoles
      ? entityRoleLabels
      : shouldDelayGcpRoleDisplay
      ? []
      : gcpFetchedRoles.length > 0
        ? gcpFetchedRoles
        : displayRoles;
  const effectiveDisplayRoles = Array.from(new Set([
    ...baseDisplayRoles
      .filter((role) => !locallyRemovedRoles.some((removedRole) => isSameRole(removedRole, role)))
      .map(getRoleDisplayName),
    ...locallyAddedRoles.map(getRoleDisplayName),
  ]));
  const rolesDisplayText = !hasFetchedEntityRoles && shouldDelayGcpRoleDisplay
    ? "Loading roles..."
    : effectiveDisplayRoles.length > 0
      ? effectiveDisplayRoles.slice(0, 2).join(", ")
      : "No roles assigned";

  useEffect(() => {
    if (!hasFetchedEntityRoles) return;
    setLocallyRemovedRoles((current) => {
      const next = current.filter((role) => entityRoles.some((item) => isSameRole(item.role, role)));
      return next.length === current.length ? current : next;
    });
    setLocallyAddedRoles((current) => {
      const next = current.filter((role) => !entityRoles.some((item) => isSameRole(item.role, role)));
      return next.length === current.length ? current : next;
    });
  }, [entityRoles, hasFetchedEntityRoles]);
  const formattedSyncedAt = useMemo(
    () =>
      formatDisplayDate(
        syncedAt ||
        details?.syncedAt ||
        details?.item?.syncedAt ||
        details?.item?.serviceDetails?.syncedAt ||
        details?.serviceDetails?.syncedAt ||
        details?.sa?.syncedAt ||
        details?.sa?.serviceDetails?.syncedAt ||
        extrasdetails?.syncedAt ||
        details?.createdAt ||
        details?.item?.createDate ||
        details?.item?.createdAt ||
        details?.item?.serviceDetails?.createDate ||
        details?.serviceDetails?.createDate ||
        details?.sa?.createDate ||
        details?.sa?.serviceDetails?.createDate ||
        extrasdetails?.createDate,
      ),
    [details, extrasdetails, syncedAt],
  );

  const convertProtobufTimestamp = (str: any) => {
    try {
      const seconds = parseInt(str?.match(/seconds:\s*(\d+)/)?.[1] || 0);
      const nanos = parseInt(str?.match(/nanos:\s*(\d+)/)?.[1] || 0);
      return new Date(seconds * 1000 + nanos / 1e6).toLocaleString("en-GB");
    } catch {
      return "Invalid date";
    }
  };

  const removeRoleFromUi = useCallback((role: string) => {
    queryClient.setQueryData(entityRoleQueryKey, (current: any) =>
      Array.isArray(current)
        ? current.filter((item) => !isSameRole(item?.role, role))
        : current,
    );
    queryClient.setQueryData(suggestionQueryKey, (current: any) => {
      if (!current) return current;
      const nextSuggestions = Object.entries(current.suggestions || {}).reduce(
        (result: Record<string, string[]>, [scope, value]) => {
          const remaining = (Array.isArray(value) ? value : String(value).split(","))
            .map((item) => String(item).trim())
            .filter((item) => item && !isSameRole(item, role));
          if (remaining.length > 0) result[scope] = remaining;
          return result;
        },
        {},
      );
      return { ...current, suggestions: nextSuggestions };
    });
    queryClient.setQueryData(resolutionQueryKey, (current: any) => {
      if (!current) return current;
      const nextResolutions = Object.entries(current.resolutions || {}).reduce(
        (result: Record<string, any>, [currentRole, value]) => {
          if (!isSameRole(currentRole, role)) result[currentRole] = value;
          return result;
        },
        {},
      );
      return { ...current, resolutions: nextResolutions };
    });
    setLocallyRemovedRoles((current) => Array.from(new Set([...current, role])));
    queryClient.invalidateQueries(entityRoleQueryKey);
    queryClient.invalidateQueries(suggestionQueryKey);
    queryClient.invalidateQueries(resolutionQueryKey);
  }, [entityRoleQueryKey, queryClient, resolutionQueryKey, suggestionQueryKey]);

  const handleDetach = (
    type: RoleOperation,
    role: string,
    resource?: string,
    replacementRoles?: string[],
  ) => {
    setSelectedDetach({ type, role, resource, replacementRoles });
    setDetachDialogOpen(true);
  };

  const handleDetachConfirm = async () => {
    if (!selectedDetach || isSubmittingOperation) return;
    if (selectedDetach.type === "role") {
      if (!latestTenant || !resolvedProjectId || !entityRiskParam) {
        toast({
          title: "Operation unavailable",
          description: "The entity could not be identified. Refresh the page and try again.",
          variant: "destructive",
        });
        return;
      }
      try {
        setIsSubmittingOperation(true);
        await axios.post(`${NGROK}/api/cloud/analytics/roles/remove`, {
          cloudType: cloudProvider.toUpperCase(),
          tenantName: latestTenant,
          cloudAccountId: resolvedProjectId,
          entity: entityRiskParam,
          role: selectedDetach.role,
          scope: selectedDetach.resource,
        });
        removeRoleFromUi(selectedDetach.role);
        setDetachDialogOpen(false);
        setSelectedDetach(null);
        toast({
          title: "Role removed",
          description: `${selectedDetach.role} was removed successfully.`,
        });
      } catch (error: any) {
        toast({
          title: "Role removal failed",
          description: error?.response?.data?.message || error?.response?.data?.error || "Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsSubmittingOperation(false);
      }
      return;
    }
    const analytics = selectedDetach.type === "resolve" ? entityResolutions : entitySuggestions;
    const operationEntity =
      analytics?.entity ||
      analytics?.userId ||
      analytics?.gcpUserEmail ||
      analyticsEntity;
    if (!analytics?.id || !operationEntity) {
      toast({
        title: "Operation unavailable",
        description: "The analytics record could not be identified. Refresh the page and try again.",
        variant: "destructive",
      });
      return;
    }
    const queryKey = selectedDetach.type === "resolve" ? resolutionQueryKey : suggestionQueryKey;
    const endpoint = selectedDetach.type === "resolve" ? "resolutions/resolve" : "suggestions/remove";
    try {
      setIsSubmittingOperation(true);
      await axios.post(`${NGROK}/api/cloud/analytics/${endpoint}`, {
        analyticsId: analytics.id,
        cloudType: cloudProvider.toUpperCase(),
        tenantName: latestTenant,
        cloudAccountId: resolvedProjectId,
        entity: operationEntity,
        role: selectedDetach.role,
        scope: selectedDetach.resource,
      });
      queryClient.setQueryData(queryKey, (current: any) => ({ ...(current || {}), status: "IN_PROGRESS" }));
      setActiveOperation({
        ...selectedDetach,
        type: selectedDetach.type === "resolve" ? "resolve" : "suggest",
      });
      setDetachDialogOpen(false);
      setSelectedDetach(null);
    } catch (error: any) {
      toast({
        title: "Operation failed to start",
        description: error?.response?.data?.message || error?.response?.data?.error || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmittingOperation(false);
    }
  };

  useEffect(() => {
    if (!activeOperation) return;
    const status = activeOperation.type === "resolve" ? entityResolutions?.status : entitySuggestions?.status;
    if (status === "IN_PROGRESS") return;
    if (status === "FAILED") {
      toast({
        title: activeOperation.type === "resolve" ? "Role resolution failed" : "Role removal failed",
        description: `${activeOperation.role} was not changed. You can try again.`,
        variant: "destructive",
      });
    } else {
      removeRoleFromUi(activeOperation.role);
      if (activeOperation.type === "resolve" && activeOperation.replacementRoles?.length) {
        setLocallyAddedRoles((current) => Array.from(new Set([...current, ...activeOperation.replacementRoles!])))
      }
      toast({
        title: activeOperation.type === "resolve" ? "Role resolved" : "Role removed",
        description: `${activeOperation.role} was updated successfully.`,
      });
      queryClient.invalidateQueries(["gcp-nhi-roles", latestTenant, resolvedProjectId]);
    }
    setActiveOperation(null);
  }, [
    entityResolutions?.status,
    entitySuggestions?.status,
    activeOperation,
    latestTenant,
    queryClient,
    removeRoleFromUi,
    resolvedProjectId,
    toast,
  ]);

  useEffect(() => {
    setActiveOperation(null);
    setLocallyRemovedRoles([]);
    setLocallyAddedRoles([]);
    setRolesExpanded(false);
  }, [cloudProvider, email, entityId, resolvedProjectId, latestTenant]);

  const getHeaderMetaItems = () => {
    switch (cloudNHIType) {
      case "SECRET":
        return [
          {
            label: cloudProvider.toUpperCase() === "AWS" ? "Secret ARN" : "Secret Name",
            value:
              cloudProvider.toUpperCase() === "AWS"
                ? secretName
                : extrasdetails?.displayName || extrasdetails?.name,
          },
          {
            label: "Owner",
            value:
              extrasdetails?.owner ||
              extrasdetails?.projectId ||
              extrasdetails?.tenantName ||
              entityName,
          },
          {
            label: "Created",
            value: extrasdetails?.createdAt ||
              convertProtobufTimestamp(extrasdetails?.createTime),
          },
          {
            label: "Replication",
            value: extrasdetails?.replicationType || "-",
          },
        ];

      case "TOKEN":
        return [
          {
            label: "Service Account",
            value: extrasdetails?.serviceAccountEmail || serviceAccountEmail,
          },
          {
            label: "Key ID",
            value: cloudTokenDetails?.keyId || extrasdetails?.keyId,
          },
          {
            label: "Valid After",
            value: extrasdetails?.validAfter
              ? new Date(extrasdetails.validAfter).toLocaleString("en-GB")
              : "-",
          },
          {
            label: "Managed By",
            value: extrasdetails?.isManagedKey ? "Google Managed" : "User Managed",
          },
        ];

      case "K8SERVICE":
      case "K8TOKEN":
      case "K8SECRET": {
        const item =
          cloudNHIType === "K8SERVICE"
            ? serviceDetails
            : cloudNHIType === "K8TOKEN"
              ? tokenDetails
              : secretDetails;

        return [
          { label: "Name", value: item?.name },
          { label: "Namespace", value: item?.namespace },
          { label: "Cluster", value: item?.clusterId },
          {
            label: "Last Activity",
            value: item?.lastActivity || item?.syncedAt || "-",
          },
        ];
      }

      case "OIDC":
        return [
          { label: "URL", value: oidc?.url },
          { label: "Provider ARN", value: oidc?.providerArn || "-" },
          { label: "Client Id", value: oidc?.clientIds?.join(", ") || "-" },
          {
            label: "Created At",
            value: oidc?.createDate
              ? new Date(oidc.createDate).toLocaleString("en-GB")
              : "-",
          },
        ];

      default: {
        const defaultFields = [];

        if (entityType !== "nhi") {
          defaultFields.push({
            label: cloudProvider.toUpperCase() === "AWS" ? "Role Name" : "Account Email",
            value: serviceAccountEmail || getEntityName(),
          });
          defaultFields.push({
            label: "Roles",
            value: rolesDisplayText,
          });
          defaultFields.push({
            label: "Synced At",
            value: formattedSyncedAt || "-",
          });
          defaultFields.push({
            label: cloudProvider.toUpperCase() === "AZURE" ? "Subscription" : cloudProvider.toUpperCase() === "AWS" ? "Account" : "Project",
            value: entityName,
          });
        }

        return defaultFields;
      }
    }
  };

  const getEntityName = () => {
    switch (cloudNHIType) {
      case "SECRET":
        // AWS uses secret ARN, others use displayName
        return cloudProvider?.toUpperCase() === "AWS"
          ? secretName
          : extrasdetails?.displayName || extrasdetails?.name;

      case "TOKEN":
        return extrasdetails?.serviceAccountEmail;

      case "K8SERVICE":
      case "K8TOKEN":
      case "K8SECRET":
        const item =
          cloudNHIType === "K8SERVICE"
            ? serviceDetails
            : cloudNHIType === "K8TOKEN"
              ? tokenDetails
              : secretDetails;
        return item?.name;

      case "OIDC":
        return oidc?.url || oidc?.providerArn || oidc?.clientIds?.[0];

      default:
        // Fallback → email or service account email
        return (
          serviceAccountEmail ||
          extrasdetails?.serviceAccountEmail ||
          "-"
        );
    }
  };


  const isValidDate = Boolean(formattedSyncedAt);
  const headerMetaItems = getHeaderMetaItems().filter((item) => item?.value !== undefined);
  const compactSecretMetaItems = headerMetaItems.filter(
    (item) => !["Secret Name", "Secret ARN", "Name"].includes(item.label),
  );
  const discoverySecretName =
    extrasdetails?.fullName ||
    extrasdetails?.secretDetails?.name ||
    details?.cloudSecret?.fullName ||
    details?.cloudSecret?.secretDetails?.name ||
    secretName ||
    extrasdetails?.name ||
    entityId;
  const {
    data: secretDiscoveryData,
    isLoading: isSecretDiscoveryLoading,
  } = useQuery(
    ["secret-discovery", cloudProvider, resolvedProjectId, latestTenant, discoverySecretName],
    () => fetchSecretDiscovery({
      cloudProvider,
      secretName: discoverySecretName,
      projectId: resolvedProjectId,
      tenantName: latestTenant,
    }),
    {
      enabled: isSecretAnalytics && Boolean(discoverySecretName),
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  );
  const rawManagedSecretValue =
    extrasdetails?.secretDetails?.isManagedSecret ??
    extrasdetails?.cloudSecretDetails?.isManagedSecret ??
    extrasdetails?.isManagedSecret;
  const isAdminManagedSecret = Boolean(
    extrasdetails?.secretDetails?.isAdminManaged ??
    extrasdetails?.cloudSecretDetails?.isAdminManaged ??
    extrasdetails?.isAdminManaged
  );
  const secretManagementLabel = isAdminManagedSecret
    ? "Admin Managed"
    : rawManagedSecretValue === false || rawManagedSecretValue === undefined || String(rawManagedSecretValue).toLowerCase().includes("user")
      ? "User Managed"
      : "Managed";
  const correlationIdentifiers = Array.from(
    new Set(
      [
        entityId,
        email,
        entityName,
        getEntityName(),
        serviceAccountEmail,
        principalId,
        secretName,
        cloudTokenDetails?.keyId,
        extrasdetails?.displayName,
        extrasdetails?.name,
        oidcARN,
        oidc?.url,
      ].filter(Boolean)
    )
  ) as string[];

  const entityAlerts = useMemo(() => {
    return rawAlerts.filter((alert: CloudAnomalyAlert) =>
      correlationIdentifiers.some((id) =>
        alert.entity?.toLowerCase().includes(id.toLowerCase())
      )
    );
  }, [rawAlerts, correlationIdentifiers]);
  const entityAlertScope = `${cloudProvider}:${latestTenant}:${resolvedProjectId}:${entityType}:${entityId || email || analyticsEntity}`;
  const retainedEntityAlertsRef = useRef<{
    scope: string;
    alerts: CloudAnomalyAlert[];
  }>({ scope: entityAlertScope, alerts: [] });

  if (retainedEntityAlertsRef.current.scope !== entityAlertScope) {
    retainedEntityAlertsRef.current = { scope: entityAlertScope, alerts: [] };
  }

  if (entityAlerts.length > 0) {
    retainedEntityAlertsRef.current.alerts = entityAlerts;
  }

  const displayedEntityAlerts =
    entityAlerts.length > 0
      ? entityAlerts
      : retainedEntityAlertsRef.current.alerts;

  const renderDistributionCard = ({
    title,
    description,
    items,
    total,
    emptyLabel = "No data found",
    loading = false,
    titleClassName = "text-base font-medium",
  }: {
    title: string;
    description: string;
    items: [string, number][];
    total: number;
    emptyLabel?: string;
    loading?: boolean;
    titleClassName?: string;
  }) => (
    <Card className="cea-card-surface">
      <CardHeader className="border-b pb-4">
        <CardTitle className={titleClassName}>
          {title}
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          {description}
        </p>
      </CardHeader>
      {loading ? (
        <CardContent className="py-8">
          <div className="flex flex-col items-center gap-6">
            <Skeleton className="w-56 h-56 rounded-full" />
            <div className="w-full space-y-2.5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="w-3 h-3 rounded-full flex-shrink-0" />
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-4 w-12 flex-shrink-0" />
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      ) : (
      <CardContent className="py-8">
        <div className="flex flex-col items-center gap-6">
          <div className="relative w-56 h-56">
            <svg
              viewBox="0 0 200 200"
              className="transform -rotate-90"
            >
              {items.map(([name, count], idx) => {
                const percentage =
                  total > 0 ? (Number(count) / total) * 100 : 0;

                const prevPercentages = items
                  .slice(0, idx)
                  .reduce(
                    (sum, [, c]) =>
                      sum +
                      (total > 0 ? (Number(c) / total) * 100 : 0),
                    0
                  );

                const circumference = 2 * Math.PI * 70;
                const offset =
                  circumference *
                  (1 - percentage / 100);
                const rotation =
                  (prevPercentages / 100) * 360;

                return (
                  <circle
                    key={String(name)}
                    cx="100"
                    cy="100"
                    r="70"
                    fill="none"
                    stroke={DONUT_COLORS[idx]}
                    strokeWidth="32"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    style={{
                      transformOrigin: "100px 100px",
                      transform: `rotate(${rotation}deg)`,
                    }}
                  />
                );
              })}
            </svg>

            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                {total !== 0 ? (
                  <>
                    <div className="text-2xl font-bold">
                      {total.toLocaleString()}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Total Events
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-muted-foreground mt-1">
                    {emptyLabel}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="w-full space-y-2.5">
            {items.length === 0 && (
              <div className="text-sm text-muted-foreground text-center">
                {emptyLabel}
              </div>
            )}
            {items.map(([name, count], idx) => {
              const percentage =
                total > 0 ? (Number(count) / total) * 100 : 0;

              return (
                <div
                  key={String(name)}
                  className="flex items-center gap-3 group cursor-default"
                  title={`${Number(
                    count
                  ).toLocaleString()} events`}
                >
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor:
                        DONUT_COLORS[idx],
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate font-medium">
                      {String(name)}
                    </div>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-3">
                    <span className="text-xs text-muted-foreground">
                      {Number(count).toLocaleString()}
                    </span>
                    <span className="text-sm font-semibold">
                      {percentage.toFixed(1)}%
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
      )}
    </Card>
  );


  return (
    <div className="cea-page cloud-entity-page">
      {/* ── Header ── */}
      <div className="cea-header">
        <div className="cea-header-identity">
          <button
            className="cea-back-icon-btn"
            onClick={() => window.history.back()}
            data-testid="button-back"
            aria-label="Back"
            title="Back"
          >
            <ArrowLeft />
          </button>

          <div className="cea-avatar">
            <CloudIcon className={`w-6 h-6 ${(cloudConfig[cloudProvider]?.color) ?? "text-gray-400"}`} />
          </div>

          <div className="cea-entity-info">
            <div className="cea-entity-name">
              {entityType === "nhi" ? getEntityName() : cloudProvider === "aws" ? `${email} (${entityId || "-"})` : cloudProvider === "azure" ? azureUserAnalyticsIdentifier : email}
            </div>
            <div className="cea-entity-sub">
              <span className="cea-badge provider">{cloudProvider.toUpperCase()}</span>
              {normalizedCloudNhiType === "SECRET" && (
                <>
                  <span className="cea-badge provider">{extrasdetails?.type || "Secret Manager"}</span>
                  <span className="cea-badge provider">{secretManagementLabel}</span>
                </>
              )}
              {isSecretAnalytics && (secretDiscoveryData?.locations.length || 0) > 0 && (
                <span className="cea-badge discovery">
                  <ScanSearch className="h-3 w-3" />
                  {secretDiscoveryData?.locations.length} locations found
                </span>
              )}
            </div>
            {cloudProvider === "azure" && entityType !== "nhi" && (
              <div className="flex items-center gap-2 mt-1 mb-2 flex-wrap">
                <span className="cea-roles-label">Email:</span>
                <span className="cea-role-chip lowercase">{details?.userDetails?.mail || details?.email || "No email"}</span>
                <span className="cea-roles-label ml-2">Azure Id:</span>
                <span className="cea-role-chip font-mono lowercase">{entityId}</span>
              </div>
            )}

            {(entityType !== "nhi" || !cloudNHIType || cloudNHIType === "SERVICE_ACCOUNT") && (
              <>
                <div className="cea-roles-row">
                  <span className="cea-roles-label">Roles:</span>
                  {shouldDelayGcpRoleDisplay ? (
                    <span className="cea-role-chip">Loading…</span>
                  ) : effectiveDisplayRoles.length > 0 ? (
                    <>
                      {effectiveDisplayRoles.slice(0, 4).map((role, idx) => (
                        <span key={idx} className="cea-role-chip">{role || "Unknown"}</span>
                      ))}
                      {effectiveDisplayRoles.length > 4 && (
                        <span className="cea-role-chip">+{effectiveDisplayRoles.length - 4} more</span>
                      )}
                    </>
                  ) : (
                    <span className="cea-role-chip">No roles</span>
                  )}
                </div>

                {addedRoles.length > 0 && (
                  <div className="cea-roles-row">
                    <span className="cea-roles-label">Roles Added:</span>
                    <>
                      {addedRoles.slice(0, 4).map((role: string, idx: number) => (
                        <span key={idx} className="text-[11px] font-medium px-2.5 py-[3px] rounded-lg border whitespace-nowrap bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">{role}</span>
                      ))}
                      {addedRoles.length > 4 && (
                        <span className="cea-role-chip">+{addedRoles.length - 4} more</span>
                      )}
                    </>
                  </div>
                )}

                {removedRoles.length > 0 && (
                  <div className="cea-roles-row">
                    <span className="cea-roles-label">Roles Removed:</span>
                    <>
                      {removedRoles.slice(0, 4).map((role: string, idx: number) => (
                        <span key={idx} className="text-[11px] font-medium px-2.5 py-[3px] rounded-lg border whitespace-nowrap line-through bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800">{role}</span>
                      ))}
                      {removedRoles.length > 4 && (
                        <span className="cea-role-chip">+{removedRoles.length - 4} more</span>
                      )}
                    </>
                  </div>
                )}
              </>
            )}

            {isSecretAnalytics && compactSecretMetaItems.length > 0 && (
              <div className="cea-roles-row">
                {compactSecretMetaItems.map((item) => (
                  <div key={item.label} className="flex min-w-0 items-center gap-1.5">
                    <span className="cea-roles-label">{item.label}:</span>
                    <span className="cea-role-chip max-w-full truncate" title={String(item.value || "–")}>
                      {item.value || "–"}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {entityType === "nhi" && !isSecretAnalytics && headerMetaItems.length > 0 && (
              <div className="cea-meta-grid">
                {headerMetaItems.map((item) => (
                  <div key={item.label} className="cea-meta-item">
                    <div className="cea-meta-label">{item.label}</div>
                    <div className="cea-meta-value">{item.value || "–"}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex-shrink-0 self-start ml-auto flex flex-col items-end gap-2">
            {isValidDate && (
              <div className="bg-muted/30 border border-border rounded-lg px-3 py-1.5 text-[12px] text-muted-foreground flex items-center gap-1.5 shadow-sm transition-colors hover:bg-muted/50">
                <span className="font-medium text-foreground/80">Synced:</span>
                <span>{formattedSyncedAt}</span>
              </div>
            )}
            <div className="bg-muted/30 border border-border rounded-lg px-3 py-1.5 text-[12px] text-muted-foreground flex items-center gap-1.5 shadow-sm transition-colors hover:bg-muted/50">
              <span className="font-medium text-foreground/80">
                {cloudType?.toUpperCase() === "AWS" ? "Account" : cloudType?.toUpperCase() === "AZURE" ? "Subscription" : "Project"}:
              </span>
              <span className="text-foreground/90">{entityName}</span>
            </div>
          </div>
        </div>
      </div>



      {/* ── Tab Bar ── */}
      <div className="cea-tabs">
        {isSecretAnalytics && (
          <button className={`cea-tab ${activeTab === "discovery" ? "active" : ""}`} onClick={() => setActiveTab("discovery")} data-testid="tab-discovery">
            <ScanSearch />
            Discovery
            {(secretDiscoveryData?.locations.length || 0) > 0 && (
              <span className="cea-tab-count">{secretDiscoveryData?.locations.length}</span>
            )}
          </button>
        )}
        <button className={`cea-tab ${activeTab === "details" ? "active" : ""}`} onClick={() => setActiveTab("details")} data-testid="tab-details">
          <Info />
          Details
        </button>
        <button className={`cea-tab ${activeTab === "overview" ? "active" : ""}`} onClick={() => setActiveTab("overview")} data-testid="tab-overview">
          <Activity />
          Overview
        </button>
        <button className={`cea-tab ${activeTab === "activity" ? "active" : ""}`} onClick={() => setActiveTab("activity")} data-testid="tab-activity">
          <Activity />
          Recent Activity
        </button>
        <button className={`cea-tab ${activeTab === "correlations" ? "active" : ""}`} onClick={() => setActiveTab("correlations")} data-testid="tab-correlations">
          <Network />
          Correlations
        </button>
      </div>


      {/* DISCOVERY TAB — secret entities only */}
      {isSecretAnalytics && activeTab === "discovery" && (
        <div className="cea-content">
          <SecretDiscovery data={secretDiscoveryData} isLoading={isSecretDiscoveryLoading} />
        </div>
      )}


      {/* ── OVERVIEW TAB ── */}
      {activeTab === "overview" && (() => {
        return (
          <div className="cea-content">
            <div className="cea-overview">

              {/* Entity Alerts Section — live API */}
              {(isAlertsLoading || displayedEntityAlerts.length > 0) && <Card className="cea-risk-panel">
                <CardHeader className="border-b">
                  <div>
                    <div className="cea-section-title">
                      Entity Security Alerts
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Security alerts associated with this entity
                    </p>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  {isAlertsLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                      {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="h-[180px] bg-muted/40 animate-pulse rounded-xl border border-border" />
                      ))}
                    </div>
                  ) : displayedEntityAlerts.length === 0 ? (
                    <div className="flex items-center justify-center py-6 text-muted-foreground text-sm opacity-50 select-none">
                      No alerts found for this entity
                    </div>
                  ) : (
                    <AlertGrouping
                      alerts={displayedEntityAlerts}
                      onViewAlertDetails={(alert) => {
                        setSelectedAlert(alert);
                        setIsAlertDetailsOpen(true);
                      }}
                      searchValue={alertSearchValue}
                      onSearchChange={setAlertSearchValue}
                      severityFilter={alertSeverityFilter}
                      onSeverityFilterChange={setAlertSeverityFilter}
                      groupingMode={groupingMode}
                      onGroupingModeChange={setGroupingMode}
                      hideEntityGroup
                      hideSearch
                    />
                  )}
                </CardContent>
              </Card>}

              {/* Charts Row */}
              {(showAlertsTriggered || showSaasCorrelations) && <div className="cea-charts-row">
                {showAlertsTriggered && <div className="cea-chart-card">
                  <div className="cea-chart-header">
                    <div>
                      <div className="cea-risk-title mb-1">Alerts Triggered</div>
                      <div className="cea-chart-sub">Last 7 Days</div>
                    </div>
                  </div>
                  {isEntityCardsLoading ? (
                     <div className="flex items-center justify-center h-[280px] min-h-[280px] text-muted-foreground text-sm opacity-50">Loading...</div>
                  ) : alertsData.length > 0 ? (
                     <div className="h-[280px] min-h-[280px] w-full pt-2">
                       <ResponsiveContainer width="100%" height="100%">
                         <AreaChart data={alertsData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                           <defs>
                             <linearGradient id="alertsGrad" x1="0" y1="0" x2="0" y2="1">
                               <stop offset="5%" stopColor={triggeredColor} stopOpacity={0.3}/>
                               <stop offset="95%" stopColor={triggeredColor} stopOpacity={0}/>
                             </linearGradient>
                           </defs>
                           <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} dy={5} />
                           <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} allowDecimals={false} domain={[0, maxAlerts === 0 ? 1 : maxAlerts + 1]} />
                           <RechartsTooltip contentStyle={{ borderRadius: '8px', fontSize: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1, strokeDasharray: '4 4' }} />
                           <Area type="monotone" dataKey="value" stroke={triggeredColor} strokeWidth={2} fillOpacity={1} fill="url(#alertsGrad)" />
                         </AreaChart>
                       </ResponsiveContainer>
                     </div>
                  ) : (
                    <div className="flex items-center justify-center h-[280px] min-h-[280px] text-muted-foreground text-sm opacity-50 select-none">
                      No data available
                    </div>
                  )}
                </div>}

                {showSaasCorrelations && <div className="cea-chart-card">
                  <div className="cea-chart-header">
                    <div>
                      <div className="cea-corr-title mb-1">SaaS Correlations</div>
                      <div className="cea-chart-sub">Last 7 Days</div>
                    </div>
                  </div>
                  {isEntityCardsLoading ? (
                     <div className="flex items-center justify-center h-[280px] min-h-[280px] text-muted-foreground text-sm opacity-50">Loading...</div>
                  ) : saasData.length > 0 ? (
                     <div className="h-[280px] min-h-[280px] w-full pt-2">
                       <ResponsiveContainer width="100%" height="100%">
                         <AreaChart data={saasData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                           <defs>
                             <linearGradient id="saasGrad" x1="0" y1="0" x2="0" y2="1">
                               <stop offset="5%" stopColor={corrColor} stopOpacity={0.3}/>
                               <stop offset="95%" stopColor={corrColor} stopOpacity={0}/>
                             </linearGradient>
                           </defs>
                           <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} dy={5} />
                           <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} allowDecimals={false} domain={[0, maxSaas === 0 ? 1 : maxSaas + 1]} />
                           <RechartsTooltip contentStyle={{ borderRadius: '8px', fontSize: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1, strokeDasharray: '4 4' }} />
                           <Area type="monotone" dataKey="value" stroke={corrColor} strokeWidth={2} fillOpacity={1} fill="url(#saasGrad)" />
                         </AreaChart>
                       </ResponsiveContainer>
                     </div>
                  ) : (
                    <div className="flex items-center justify-center h-[280px] min-h-[280px] text-muted-foreground text-sm opacity-50 select-none">
                      No data available
                    </div>
                  )}
                </div>}
              </div>}

              {/* Metrics Grid — coming soon */}
              <div className="cea-metrics-grid">
                {/* Potential Risk */}
                <div className="cea-risk-panel">
                  <div className="cea-risk-header">
                    <div className="cea-risk-header-left">
                      <div className="cea-risk-icon-wrap"><AlertTriangle /></div>
                      <span className="cea-risk-title">Potential Risk</span>
                    </div>
                  </div>
                  <div className="cea-risk-body p-4 min-h-[280px]">
                    {isEntityCardsLoading ? (
                      <div className="flex items-center justify-center py-6 text-muted-foreground text-sm opacity-50">Loading...</div>
                    ) : potentialRisks.length > 0 ? (
                      <div className="space-y-3 px-1">
                        {potentialRisks.map((risk: string, i: number) => (
                          <div key={i} className="cea-inner-surface relative flex items-start gap-4 p-4 overflow-hidden">
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500/80" />
                            <div className="flex-shrink-0 mt-0.5 ml-1 p-2 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-100 dark:border-red-900/30">
                              <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />
                            </div>
                            <span className="text-[13.5px] text-foreground leading-relaxed font-medium opacity-90">{risk}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-6 text-muted-foreground text-sm opacity-50 select-none">
                        No risk data available
                      </div>
                    )}
                  </div>
                </div>

                {/* Insights */}
                <div className="cea-insights-panel">
                  <div className="cea-insights-header">
                    <div className="cea-insights-icon-wrap"><Lightbulb /></div>
                    <span className="cea-insights-title">Insights</span>
                    <Sparkles className="w-3.5 h-3.5 ml-0.5 opacity-60" style={{ color: "hsl(var(--primary))" }} />
                  </div>
                  <div className="cea-insights-body p-4 min-h-[280px]">
                    {isEntityCardsLoading ? (
                      <div className="flex items-center justify-center py-6 text-muted-foreground text-sm opacity-50">Loading...</div>
                    ) : insights.length > 0 ? (
                      <div className="space-y-3 px-1">
                        {insights.map((insight: string, i: number) => (
                          <div key={i} className="cea-inner-surface relative flex items-start gap-4 p-4 overflow-hidden">
                            <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500/80" />
                            <div className="flex-shrink-0 mt-0.5 ml-1 p-2 bg-blue-50 dark:bg-blue-950/30 rounded-lg border border-blue-100 dark:border-blue-900/30">
                              <Lightbulb className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                            </div>
                            <span className="text-[13.5px] text-foreground leading-relaxed font-medium opacity-90">{insight}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-6 text-muted-foreground text-sm opacity-50 select-none">
                        No insights available
                      </div>
                    )}
                  </div>
                </div>

              </div>

              <div className="flex justify-center pt-2 pb-4">
                <div className="relative inline-flex group">
                  <span className="inline-flex">
                    <Button
                      onClick={handleGenerateRiskInsights}
                      disabled={isRiskInsightButtonDisabled}
                      className="gap-2 px-6 h-11 rounded-xl font-semibold shadow-sm"
                    >
                      {(isRiskInsightInProgress || isTriggeringRiskInsight) ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Generating Potential Risk and Insights...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          Generate Potential Risk and Insights
                        </>
                      )}
                    </Button>
                  </span>
                  {riskInsightBlockedReason && (
                    <div className="pointer-events-none absolute left-1/2 top-0 z-50 hidden -translate-x-1/2 -translate-y-[calc(100%+8px)] whitespace-nowrap rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md group-hover:block group-focus-within:block">
                      {riskInsightBlockedReason}
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        );
      })()}

      {/* DETAILS TAB */}
      {activeTab === "details" && (
        <div className="cea-content">
          <div className="space-y-6">
            {/* Top Resources + Top Permissions */}
            <div className="grid grid-cols-2 gap-6">
              {renderDistributionCard({
                title: "Top Resources",
                description: `Most accessed resources by ${entityType === "nhi" ? "service account" : "user"}`,
                items: topResources as [string, number][],
                total: totalResourceUsage,
                loading: isTopResourcesLoading,
                titleClassName: "text-lg font-semibold",
              })}
              <Card className="cea-card-surface">
                <CardHeader className="border-b pb-4">
                  <CardTitle className="text-lg font-semibold">
                    Top Permissions
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    Most frequently used permissions by{" "}
                    {entityType === "nhi"
                      ? "service account"
                      : "user"}
                  </p>
                </CardHeader>
                {isTopPermissionsLoading ? (
                  <CardContent className="py-6">
                    <div className="space-y-3">
                      {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} className="space-y-1.5">
                          <div className="flex items-baseline justify-between gap-3">
                            <Skeleton className="h-4 flex-1" />
                            <Skeleton className="h-4 w-10 flex-shrink-0" />
                          </div>
                          <Skeleton className="h-2 w-full rounded-sm" />
                        </div>
                      ))}
                    </div>
                  </CardContent>
                ) : (
                <CardContent className="py-6">
                  <div className="space-y-3">
                    {topPermissions.map(
                      ([permission, count]) => {
                        const percentage =
                          (Number(count) /
                            totalPermissionUsage) *
                          100;

                        return (
                          <div
                            key={String(permission)}
                            className="space-y-1.5 cursor-default"
                            title={`${Number(
                              count
                            ).toLocaleString()} uses`}
                          >
                            <div className="flex items-baseline justify-between gap-3">
                              <div className="text-sm font-medium font-mono truncate flex-1">
                                {String(permission)}
                              </div>
                              <div className="flex-shrink-0">
                                <span className="text-sm font-semibold">
                                  {percentage.toFixed(1)}%
                                </span>
                              </div>
                            </div>
                            <div className="bg-muted rounded-sm h-2 overflow-hidden">
                              <div
                                className="bg-primary h-full rounded-sm transition-all"
                                style={{
                                  width: `${percentage}%`,
                                }}
                              />
                            </div>
                          </div>
                        );
                      }
                    )}
                  </div>
                </CardContent>
                )}
              </Card>
            </div>

            {isSecretAnalytics && (
              <div className="grid grid-cols-2 gap-6">
                {renderDistributionCard({
                  title: "Top Users",
                  description: "Users accessing or interacting with this secret",
                  items: topSecretUsers as [string, number][],
                  total: topSecretUsers.reduce((sum, [, count]) => sum + Number(count), 0),
                  emptyLabel: "No user activity found",
                })}
                {renderDistributionCard({
                  title: "Top Service Accounts",
                  description: "Service accounts interacting with this secret",
                  items: topSecretServiceAccounts as [string, number][],
                  total: topSecretServiceAccounts.reduce((sum, [, count]) => sum + Number(count), 0),
                  emptyLabel: "No service account activity found",
                })}
              </div>
            )}

            {!isSecretAnalytics && (
              <Card className="cea-card-surface overflow-hidden">
                <CardHeader className="border-b p-0">
                  <button
                    type="button"
                    onClick={() => setRolesExpanded((expanded) => !expanded)}
                    className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left transition-colors hover:bg-muted/30"
                    aria-expanded={rolesExpanded}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400">
                        <ShieldMinus className="h-5 w-5" />
                      </div>
                      <div className="min-w-0">
                        <CardTitle className="text-lg font-semibold">Remove Roles</CardTitle>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Remove assigned roles directly. Administrator privileges are required.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-3">
                      <Badge variant="secondary" className="min-w-8 justify-center">
                        {currentEntityRoles.length}
                      </Badge>
                      {rolesExpanded ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </div>
                  </button>
                </CardHeader>
                {rolesExpanded && (
                  <CardContent className="pt-6">
                    <div className="space-y-3">
                      {currentEntityRoles.map((item, index) => (
                        <div
                          key={`${item.role}:${item.scope || index}`}
                          className="cea-inner-surface flex flex-col justify-between gap-4 p-5 sm:flex-row sm:items-center"
                        >
                          <div className="min-w-0 space-y-2">
                            <Badge
                              variant="outline"
                              className="break-all whitespace-normal bg-muted/20 px-2.5 py-1 text-left text-[13px] font-semibold text-foreground"
                            >
                              {getRoleDisplayName(item.role)}
                            </Badge>
                            {item.scope && (
                              <p className="break-all text-xs text-muted-foreground">
                                Scope: {item.scope}
                              </p>
                            )}
                          </div>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDetach("role", item.role, item.scope)}
                            className="w-full flex-shrink-0 gap-2 font-medium shadow-sm transition-transform active:scale-95 sm:w-auto"
                            data-testid={`button-remove-role-${index}`}
                          >
                            <ShieldMinus className="h-4 w-4" />
                            Remove Role
                          </Button>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                )}
              </Card>
            )}


            {!isSecretAnalytics && cloudProvider === "gcp" && resolutionItems.length > 0 && (
              <Card className="cea-card-surface cea-resolve-role-card">
                <CardHeader className="border-b">
                  <div>
                    <CardTitle className="text-lg font-semibold">
                      Resolve Project-Level Roles
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Recommended role replacements for improved
                      security and least-privilege access
                    </p>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    {paginatedResolutionItems.map((item) => {
                      const isResolving =
                        resolutionInProgress &&
                        activeOperation?.type === "resolve" &&
                        activeOperation.role === item.currentRole;

                      return (
                        <div
                          key={item.currentRole}
                          className={`cea-inner-surface overflow-hidden ${resolutionInProgress && !isResolving ? "opacity-60" : ""}`}
                        >
                          <div className="grid items-stretch lg:grid-cols-[minmax(0,1fr)_52px_minmax(0,1.35fr)_auto]">
                            <div className="flex min-w-0 flex-col justify-center gap-3 p-5 lg:p-6">
                              <div className="flex items-center gap-2">
                                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-400">
                                  <AlertTriangle className="h-4 w-4" />
                                </div>
                                <div>
                                  <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                                    Current project role
                                  </p>
                                  <p className="mt-1 break-words text-[15px] font-semibold text-foreground">
                                    {getRoleDisplayName(item.currentRole)}
                                  </p>
                                </div>
                              </div>
                              <Badge variant="outline" className="w-fit border-primary/20 bg-primary/5 text-[10px] font-bold uppercase tracking-wider text-primary">
                                Project-level
                              </Badge>
                            </div>

                            <div className="flex items-center justify-center border-y bg-muted/20 py-3 lg:border-x lg:border-y-0 lg:py-0">
                              <div className="flex h-8 w-8 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-sm">
                                <ArrowRight className="h-4 w-4 rotate-90 lg:rotate-0" />
                              </div>
                            </div>

                            <div className="flex min-w-0 flex-col justify-center gap-3 p-5 lg:p-6">
                              <div>
                                <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                                  Recommended least-privilege roles
                                </p>
                                <p className="mt-1 text-xs text-muted-foreground">
                                  Applied at the project level before the current role is removed
                                </p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                {item.suggestedRoles.map((role) => (
                                  <Badge
                                    key={role}
                                    variant="secondary"
                                    className="border border-green-200 bg-green-50 px-2.5 py-1 text-[12px] font-semibold text-green-700 hover:bg-green-50 dark:border-green-800/50 dark:bg-green-900/20 dark:text-green-400"
                                  >
                                    <Check className="mr-1.5 h-3 w-3" />
                                    {getRoleDisplayName(role)}
                                  </Badge>
                                ))}
                              </div>
                            </div>

                            <div className="flex items-center border-t p-5 lg:border-l lg:border-t-0 lg:p-6">
                              <Button
                                size="sm"
                                onClick={() => handleDetach(
                                  "resolve",
                                  item.currentRole,
                                  undefined,
                                  item.suggestedRoles,
                                )}
                                disabled={resolutionInProgress}
                                className="w-full gap-2 font-medium shadow-sm lg:w-auto"
                              >
                                {isResolving ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Check className="h-4 w-4" />
                                )}
                                {isResolving ? "Resolving..." : "Resolve Role"}
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {totalResolutionPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-muted-foreground">
                        Showing{" "}
                        {(resolutionPage - 1) *
                          ITEMS_PER_PAGE +
                          1}{" "}
                        to{" "}
                        {Math.min(
                          resolutionPage *
                          ITEMS_PER_PAGE,
                          resolutionItems.length
                        )}{" "}
                        of {resolutionItems.length} items
                      </p>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setResolutionPage((p) =>
                              Math.max(1, p - 1)
                            )
                          }
                          disabled={resolutionPage === 1}
                          data-testid="button-prev-resolution"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </Button>

                        <div className="flex items-center gap-1">
                          {Array.from(
                            {
                              length: totalResolutionPages,
                            },
                            (_, i) => i + 1
                          ).map((page) => (
                            <Button
                              key={page}
                              variant={
                                page === resolutionPage
                                  ? "default"
                                  : "outline"
                              }
                              size="sm"
                              onClick={() =>
                                setResolutionPage(page)
                              }
                              className="w-9"
                              data-testid={`button-resolution-page-${page}`}
                            >
                              {page}
                            </Button>
                          ))}
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setResolutionPage((p) =>
                              Math.min(
                                totalResolutionPages,
                                p + 1
                              )
                            )
                          }
                          disabled={
                            resolutionPage ===
                            totalResolutionPages
                          }
                          data-testid="button-next-resolution"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}


            {!isSecretAnalytics && suggestionItems.length > 0 && (
              <Card className="cea-card-surface">
                <CardHeader className="border-b">
                  <div>
                    <CardTitle className="text-lg font-semibold">
                      Suggested Role Cleanup
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Over-privileged or redundant project-level
                      roles that can be safely removed
                    </p>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    {paginatedSuggestionItems.map((item, idx) => (
                          <div
                            key={`${item.level}:${item.role}`}
                            className={`cea-inner-surface group flex flex-col sm:flex-row sm:items-center justify-between gap-6 p-5 ${suggestionInProgress && (activeOperation?.role !== item.role || activeOperation?.resource !== item.level) ? "opacity-60" : ""}`}
                          >
                            {/* Scope and Role */}
                            <div className="flex flex-col gap-2 w-full sm:w-2/3 min-w-0">
                               <div className="flex items-center gap-2">
                                 <Badge variant="outline" className="text-[10px] uppercase font-bold tracking-wider px-2 border-primary/20 text-primary bg-primary/5">{item.level}</Badge>
                                 <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                                   <Info className="w-3.5 h-3.5 opacity-70" />
                                   <span>Over-privileged access</span>
                                 </div>
                               </div>
                               <div className="flex flex-wrap gap-2 mt-1">
                                  <Badge variant="outline" className="text-[13px] font-semibold text-foreground px-2.5 py-1 bg-muted/20 border-border/50 break-all whitespace-normal text-left">
                                    {getRoleDisplayName(item.role)}
                                  </Badge>
                               </div>
                            </div>

                            {/* Action */}
                            <div className="flex-shrink-0 w-full sm:w-auto flex justify-end mt-2 sm:mt-0">
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDetach("suggest", item.role, item.level)}
                                disabled={suggestionInProgress}
                                className="gap-2 font-medium shadow-sm transition-transform active:scale-95 w-full sm:w-auto"
                                data-testid={`button-detach-suggest-${idx}`}
                              >
                                {suggestionInProgress && activeOperation?.type === "suggest" && activeOperation.role === item.role && activeOperation.resource === item.level ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <ShieldMinus className="w-4 h-4" />
                                )}
                                {suggestionInProgress && activeOperation?.type === "suggest" && activeOperation.role === item.role && activeOperation.resource === item.level
                                  ? "Removing..."
                                  : "Remove Role"}
                              </Button>
                            </div>
                          </div>
                        ))}
                  </div>

                  {/* Pagination */}
                  {totalSuggestionPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <p className="text-sm text-muted-foreground">
                        Showing{" "}
                        {(suggestionPage - 1) *
                          ITEMS_PER_PAGE +
                          1}{" "}
                        to{" "}
                        {Math.min(
                          suggestionPage *
                          ITEMS_PER_PAGE,
                          suggestionItems.length
                        )}{" "}
                        of {suggestionItems.length} items
                      </p>

                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setSuggestionPage((p) =>
                              Math.max(1, p - 1)
                            )
                          }
                          disabled={suggestionPage === 1}
                          data-testid="button-prev-suggestion"
                        >
                          <ChevronLeft className="w-4 h-4" />
                          Previous
                        </Button>

                        <div className="flex items-center gap-1">
                          {Array.from(
                            {
                              length: totalSuggestionPages,
                            },
                            (_, i) => i + 1
                          ).map((page) => (
                            <Button
                              key={page}
                              variant={
                                page === suggestionPage
                                  ? "default"
                                  : "outline"
                              }
                              size="sm"
                              onClick={() =>
                                setSuggestionPage(page)
                              }
                              className="w-9"
                              data-testid={`button-suggestion-page-${page}`}
                            >
                              {page}
                            </Button>
                          ))}
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            setSuggestionPage((p) =>
                              Math.min(
                                totalSuggestionPages,
                                p + 1
                              )
                            )
                          }
                          disabled={
                            suggestionPage ===
                            totalSuggestionPages
                          }
                          data-testid="button-next-suggestion"
                        >
                          Next
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

          </div>
        </div>
      )}

      {/* ACTIVITY TAB */}
      {activeTab === "activity" && (
        <div className="cea-content">
          <CloudRecentActivity
            url={(cloudNHIType === "K8SERVICE" || cloudNHIType === "K8SECRET") && rawLogsAPI
              ? rawLogsAPI
              : `${NGROK}/api/cloud/get-raw-logs?tenantName=${latestTenant}&cloudId=${resolvedProjectId}&entity=${encodeURIComponent(analyticsEntity || getEntityName())}&cloudType=${cloudProvider.toUpperCase()}`}
            tenantName={latestTenant}
            cloudType={cloudProvider.toUpperCase()}
            subscriptionName={latestSubList?.subscriptionName || entityName}
          />
        </div>
      )}

      {activeTab === "correlations" && (
        <div className="cea-content">
          <CloudCorrelations
            url={`${NGROK}/api/cloud/cloud-saas-correlations?tenantName=${encodeURIComponent(latestTenant)}&cloudId=${encodeURIComponent(resolvedProjectId || "")}`}
            entityIdentifiers={correlationIdentifiers}
          />
        </div>
      )}

      {/* DETACH CONFIRMATION */}
      <AlertDialog
        open={detachDialogOpen}
        onOpenChange={(open) => !isSubmittingOperation && setDetachDialogOpen(open)}
      >
        <AlertDialogContent className="min-w-0 overflow-hidden">
          <AlertDialogHeader className="min-w-0">
            <AlertDialogTitle>{selectedDetach?.type === "resolve" ? "Resolve Role" : "Remove Role"}</AlertDialogTitle>
            <AlertDialogDescription className="min-w-0 break-words">
              Are you sure you want to {selectedDetach?.type === "resolve" ? "resolve" : "remove"} role{" "}
              <span className="block max-w-full break-all font-medium text-foreground">
                {getRoleDisplayName(selectedDetach?.role || "")}
              </span>
              {selectedDetach?.resource && (
                <>
                  {" "}
                  from scope{" "}
                  <span className="block max-w-full break-all font-medium text-foreground">
                    {selectedDetach.resource}
                  </span>
                </>
              )}
              ?
              <br />
              <br />
              {selectedDetach?.type === "resolve"
                ? "The recommended least-privilege bindings will be applied before the current role is removed."
                : "This action will immediately remove the associated permissions."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="min-w-0 w-full">
            <AlertDialogCancel disabled={isSubmittingOperation} data-testid="button-cancel-detach">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(event) => {
                event.preventDefault();
                void handleDetachConfirm();
              }}
              disabled={isSubmittingOperation}
              className={selectedDetach?.type === "resolve" ? "" : "bg-red-600 hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700"}
              data-testid="button-confirm-detach"
            >
              {isSubmittingOperation && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isSubmittingOperation
                ? selectedDetach?.type === "resolve" ? "Resolving..." : "Removing..."
                : selectedDetach?.type === "resolve" ? "Resolve Role" : "Remove Role"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDetails
        alert={selectedAlert}
        open={isAlertDetailsOpen}
        onClose={() => {
          setIsAlertDetailsOpen(false);
          setSelectedAlert(null);
        }}
      />
    </div>
  );
}
